import { AlertTriangle } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import type { DeliveryNote } from "@/types";

interface MarkAsReviewedDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deliveryNote: DeliveryNote;
  hasUnmatchedItems: boolean;
  unmatchedCount: number;
  hasDifferences: boolean;
  differencesCount: number;
  onConfirm: () => void;
}

export default function MarkAsReviewedDialog({
  open,
  onOpenChange,
  deliveryNote,
  hasUnmatchedItems,
  unmatchedCount,
  hasDifferences,
  differencesCount,
  onConfirm,
}: MarkAsReviewedDialogProps) {
  const canConfirm = deliveryNote.has_purchase_request && !hasUnmatchedItems;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>¿Marcar remito como revisado?</AlertDialogTitle>
          <AlertDialogDescription className="space-y-3">
            {!deliveryNote.has_purchase_request ? (
              <Alert variant="destructive">
                <AlertTriangle />
                <AlertTitle>Solicitud de Compra requerida</AlertTitle>
                <AlertDescription>
                  Debes vincular una Solicitud de Compra antes de marcar el remito como revisado.
                </AlertDescription>
              </Alert>
            ) : hasUnmatchedItems ? (
              <Alert variant="destructive">
                <AlertTriangle />
                <AlertTitle>Items sin vincular</AlertTitle>
                <AlertDescription>
                  Hay {unmatchedCount} {unmatchedCount === 1 ? "item" : "items"} sin vincular a la solicitud de compra. Debes vincular todos los items antes de marcar el remito como revisado.
                </AlertDescription>
              </Alert>
            ) : (
              <>
                <p>Antes de continuar, asegúrate de haber:</p>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>Revisado las cantidades recibidas vs. las del remito</li>
                  <li>Vinculado la solicitud de compra correcta</li>
                  <li>Verificado el proveedor y la información general</li>
                </ul>
                {hasDifferences && (
                  <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-amber-800">
                    <AlertTriangle className="h-4 w-4 inline mr-2" />
                    Hay {differencesCount} ítem(s) con diferencias de cantidad
                  </div>
                )}
                <p className="text-amber-700 font-medium mt-3">
                  Esta acción no se puede deshacer.
                </p>
              </>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancelar</AlertDialogCancel>
          {canConfirm && (
            <AlertDialogAction onClick={onConfirm}>
              Marcar como revisado
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
